import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, Search, FileText, Users, Mail, Phone, Building2, DollarSign, Calendar,
    Filter, X, Send, MessageSquare, Download, UserCog, BarChart3, TrendingUp, UserCheck, Palette, Presentation, Flame,
    ShieldAlert, ShieldCheck, AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api, formatApiError, TOKEN_KEY, API } from "../lib/api";
import { LeadEngagementBadge, LeadEngagementCard } from "../components/EmailEngagement";
import { useBranding } from "../lib/branding";

const STATUSES = ["All", "New", "Contacted", "Appointment Set", "Sold", "Closed"];
const SALE_STATUSES = ["", "Pending", "Yes", "No"];

const statusTone = {
    New: "bg-blue-100 text-blue-800 border border-blue-200",
    Contacted: "bg-amber-100 text-amber-800 border border-amber-200",
    "Appointment Set": "bg-purple-100 text-purple-800 border border-purple-200",
    Sold: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    Closed: "bg-slate-200 text-slate-700 border border-slate-300",
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { branding } = useBranding();
    const [me, setMe] = useState(null);
    const [leads, setLeads] = useState([]);
    const [agents, setAgents] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, appointment_set: 0, sold: 0, unassigned: 0, hot: 0 });
    const [conversions, setConversions] = useState({ by_agent: [], unassigned: 0 });
    const [status, setStatus] = useState("All");
    const [agentFilter, setAgentFilter] = useState("All");
    const [search, setSearch] = useState("");
    const [hotOnly, setHotOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [comm, setComm] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                status: status === "All" ? undefined : status,
                search: search || undefined,
                agent_id: agentFilter === "All" ? undefined : agentFilter,
                hot: hotOnly ? true : undefined,
            };
            const [meR, leadsR, statsR, agentsR, convR] = await Promise.all([
                api.get("/auth/me"),
                api.get("/admin/leads", { params }),
                api.get("/admin/stats"),
                api.get("/admin/agents"),
                api.get("/admin/conversions"),
            ]);
            setMe(meR.data);
            setLeads(leadsR.data.leads);
            setStats(statsR.data);
            setAgents(agentsR.data.agents);
            setConversions(convR.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [status, search, agentFilter, hotOnly]);

    // Coalesce rapid duplicate loads (React StrictMode double-invoke in dev,
    // back-to-back filter clicks, etc.) into a single in-flight request so
    // the table doesn't flicker between data → Loading… → data.
    const inFlightRef = React.useRef(null);
    const safeLoad = useCallback(async () => {
        if (inFlightRef.current) return inFlightRef.current;
        inFlightRef.current = load().finally(() => { inFlightRef.current = null; });
        return inFlightRef.current;
    }, [load]);

    useEffect(() => { safeLoad(); }, [safeLoad]);

    // Deep-link from email "View Full Lead in Dashboard" button: ?lead=ID
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const leadId = searchParams.get("lead");
        if (!leadId || loading) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/leads/${leadId}`);
                setSelected(data);
                setComm({});
                setNoteText("");
                // Strip ?lead= so refreshing doesn't keep re-opening
                const next = new URLSearchParams(searchParams);
                next.delete("lead");
                setSearchParams(next, { replace: true });
            } catch (err) { toast.error(formatApiError(err)); }
        })();
    }, [searchParams, loading, setSearchParams]);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login", { replace: true });
    };

    const updateStatus = async (lead, newStatus) => {
        setUpdating(true);
        try {
            await api.patch(`/admin/leads/${lead.id}/status`, { status: newStatus });
            toast.success(`Marked as ${newStatus}`);
            const updated = { ...lead, status: newStatus };
            if (newStatus === "Sold") updated.sale_status = "Yes";
            setSelected(updated);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUpdating(false); }
    };

    const assign = async (agentId) => {
        try {
            await api.patch(`/admin/leads/${selected.id}/assign`, { agent_id: agentId || null });
            const ag = agents.find((a) => a.id === agentId);
            const updated = { ...selected, assigned_agent_id: agentId || null, assigned_agent_name: ag?.name || null };
            setSelected(updated);
            toast.success(agentId ? `Assigned to ${ag?.name}` : "Unassigned");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        try {
            const { data } = await api.post(`/admin/leads/${selected.id}/notes`, { text: noteText });
            const updated = { ...selected, notes: [...(selected.notes || []), data] };
            setSelected(updated);
            setNoteText("");
            toast.success("Note added");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveCommission = async () => {
        try {
            const payload = Object.fromEntries(
                Object.entries(comm)
                    .filter(([k, v]) => v !== "" && v !== undefined)
                    .map(([k, v]) => [k, k === "sale_status" ? v : (v === "" ? null : Number(v))])
            );
            const { data } = await api.patch(`/admin/leads/${selected.id}/commission`, payload);
            setSelected(data);
            setComm({});
            toast.success("Commission updated");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const exportCsv = async () => {
        try {
            const res = await api.get("/admin/export.csv", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = "leads.csv";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="font-serif text-xl text-navy-900">FMO Dashboard</div>
                        <div className="text-sm text-slate-700">Welcome, {me?.name || me?.email || "Admin"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <StripeModePill mode={stats.stripe_mode} webhookSecretSet={stats.stripe_webhook_secret_set} />
                        <Button
                            variant="outline"
                            onClick={() => window.open("/pitch?presenter=Mark%20Stevens&agent=mark-stevens", "_blank")}
                            data-testid="pitch-deck-btn"
                            className="rounded-md bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
                        >
                            <Presentation className="w-4 h-4 mr-2" /> Pitch Deck
                        </Button>
                        <Button variant="outline" onClick={() => navigate("/admin/branding")} data-testid="branding-btn" className="rounded-md">
                            <Palette className="w-4 h-4 mr-2" /> Branding
                        </Button>
                        <Button variant="outline" onClick={() => navigate("/admin/agents")} data-testid="manage-agents-btn" className="rounded-md">
                            <UserCog className="w-4 h-4 mr-2" /> Agents
                        </Button>
                        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="rounded-md">
                            <Download className="w-4 h-4 mr-2" /> Export CSV
                        </Button>
                        <Button variant="outline" onClick={logout} data-testid="logout-btn" className="rounded-md">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-10">
                <StripeModeBanner mode={stats.stripe_mode} webhookSecretSet={stats.stripe_webhook_secret_set} />
                {!branding?.owner_name && (
                    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="setup-owner-banner">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-md bg-amber-200 text-amber-900 flex items-center justify-center font-bold flex-shrink-0">1</div>
                            <div>
                                <div className="font-serif text-lg text-amber-950">Add your photo and personal details to the homepage</div>
                                <div className="text-sm text-amber-900/80 mt-1">Your homepage is generic right now. Add your name, photo, phone and bio so the "Hi, I'm [name]" section appears under the hero. Takes about 2 minutes.</div>
                            </div>
                        </div>
                        <Button onClick={() => navigate("/admin/branding")} data-testid="setup-owner-cta" className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap">
                            <UserCog className="w-4 h-4 mr-2" /> Add My Profile
                        </Button>
                    </div>
                )}

                <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-4">
                    <StatCard
                        label="Total Leads"
                        value={stats.total}
                        icon={Users}
                        active={status === "All" && agentFilter === "All" && !hotOnly}
                        onClick={() => { setStatus("All"); setAgentFilter("All"); setHotOnly(false); scrollToTable(); }}
                        testid="stat-total"
                    />
                    <StatCard
                        label="New"
                        value={stats.new}
                        icon={Mail}
                        accent="bg-blue-50 text-blue-700"
                        ringColor="ring-blue-100 border-blue-500"
                        active={status === "New" && !hotOnly}
                        onClick={() => { setStatus("New"); setHotOnly(false); scrollToTable(); }}
                        testid="stat-new"
                    />
                    <StatCard
                        label="Contacted"
                        value={stats.contacted}
                        icon={Phone}
                        accent="bg-amber-50 text-amber-700"
                        ringColor="ring-amber-100 border-amber-500"
                        active={status === "Contacted" && !hotOnly}
                        onClick={() => { setStatus("Contacted"); setHotOnly(false); scrollToTable(); }}
                        testid="stat-contacted"
                    />
                    <StatCard
                        label="Appt Set"
                        value={stats.appointment_set}
                        icon={Calendar}
                        accent="bg-purple-50 text-purple-700"
                        ringColor="ring-purple-100 border-purple-500"
                        active={status === "Appointment Set" && !hotOnly}
                        onClick={() => { setStatus("Appointment Set"); setHotOnly(false); scrollToTable(); }}
                        testid="stat-appt"
                    />
                    <StatCard
                        label="Sold"
                        value={stats.sold}
                        icon={DollarSign}
                        accent="bg-emerald-50 text-emerald-700"
                        ringColor="ring-emerald-100 border-emerald-500"
                        active={status === "Sold" && !hotOnly}
                        onClick={() => { setStatus("Sold"); setHotOnly(false); scrollToTable(); }}
                        testid="stat-sold"
                    />
                    <StatCard
                        label="Unassigned"
                        value={stats.unassigned}
                        icon={UserCheck}
                        accent="bg-slate-50 text-slate-700"
                        ringColor="ring-slate-100 border-slate-500"
                        active={agentFilter === "unassigned" && !hotOnly}
                        onClick={() => { setAgentFilter("unassigned"); setStatus("All"); setHotOnly(false); scrollToTable(); }}
                        testid="stat-unassigned"
                    />
                    <button
                        type="button"
                        onClick={() => { setHotOnly((v) => !v); scrollToTable(); }}
                        data-testid="hot-leads-stat"
                        className={`text-left bg-white border-2 rounded-2xl p-5 transition-all ${hotOnly ? "border-rose-500 ring-2 ring-rose-100 shadow-lg" : "border-slate-200 hover:border-rose-300 hover:-translate-y-0.5"}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-rose-700 flex items-center gap-1">
                                <Flame className="w-3 h-3" /> Hot Leads
                            </div>
                            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-gradient-to-br from-rose-500 to-amber-500 text-white shadow-md">
                                <Flame className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="mt-4 font-serif text-3xl text-rose-700">{stats.hot || 0}</div>
                        <div className="mt-1 text-[11px] text-slate-700">{hotOnly ? "Filter active · click to clear" : "Click to filter"}</div>
                    </button>
                </div>

                {/* FMO-only: Wealth Report revenue strip — agents never see this */}
                <div className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="wealth-strip">
                    <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-emerald-50 rounded-2xl p-5">
                        <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-amber-300/90">Wealth Report Revenue</div>
                        <div className="mt-3 font-serif text-3xl text-white" data-testid="stat-wealth-revenue">${Number(stats.wealth_report_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                        <div className="mt-1 text-[11px] text-emerald-100/70">Test mode — $29.95 × purchases</div>
                    </div>
                    <StatCard label="Reports Sold" value={stats.wealth_report_purchased || 0} icon={DollarSign} accent="bg-emerald-50 text-emerald-700" testid="stat-wealth-purchased" />
                    <StatCard label="Wealth Funnel Starts" value={stats.wealth_report_started || 0} icon={Users} accent="bg-amber-50 text-amber-700" testid="stat-wealth-started" />
                    <StatCard label="Annuity Review Leads" value={stats.annuity_review_leads || 0} icon={Mail} accent="bg-blue-50 text-blue-700" testid="stat-annuity-leads" />
                    <StatCard label="Sold Conversion %" value={`${stats.conversion_pct || 0}%`} icon={UserCheck} accent="bg-purple-50 text-purple-700" testid="stat-conversion" />
                </div>

                {/* FMO-only: Conversion funnel + Revenue Per Appointment (high-signal metrics for agent/FMO decks) */}
                <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="funnel-strip">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                        <div>
                            <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Conversion Funnel</div>
                            <div className="mt-1 font-serif text-xl text-navy-900">Review Request → Appointment → Toolkit (post-booking upsell)</div>
                        </div>
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl px-5 py-3 text-right">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800">Revenue Per Appointment</div>
                            <div className="mt-1 font-serif text-2xl text-amber-900" data-testid="stat-rev-per-appt">
                                ${Number(stats.revenue_per_appointment || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                            <div className="text-[10px] text-amber-900 mt-0.5">
                                Post-booking Toolkit: ${Number(stats.toolkit_post_booking_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="funnel-stages">
                        {[
                            { label: "Review Requested", value: stats.review_requested || 0, key: "review-requested" },
                            { label: "Appointment Scheduled", value: stats.appointment_scheduled || 0, key: "appt-scheduled", rate: stats?.funnel?.review_to_appointment },
                            { label: "Toolkit After Appointment", value: stats.toolkit_purchased_after_appointment || 0, key: "toolkit-after-appt", rate: stats?.funnel?.appointment_to_toolkit, highlight: true },
                            { label: "Standalone Scorecards", value: stats.wealth_report_purchased || 0, key: "wealth-purchased" },
                            { label: "Scorecards Completed", value: stats.wealth_report_completed || 0, key: "wealth-completed" },
                        ].map((s) => {
                            // Cap display at 100%. Seed/demo data outside the wealth funnel can
                            // inflate downstream stage counts, producing misleading >100% rates.
                            const showRate = s.rate != null && s.rate <= 100 ? s.rate : null;
                            return (
                                <div key={s.key} className={`rounded-xl p-4 ${s.highlight ? "bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300" : "bg-slate-50"}`} data-testid={`funnel-${s.key}`}>
                                    <div className={`uppercase tracking-[0.16em] text-[10px] font-bold ${s.highlight ? "text-amber-800" : "text-slate-700"}`}>{s.label}</div>
                                    <div className={`mt-2 font-serif text-2xl ${s.highlight ? "text-amber-900" : "text-navy-900"}`}>{s.value}</div>
                                    {showRate != null ? (
                                        <div className={`mt-1 text-[11px] font-medium ${s.highlight ? "text-amber-800" : "text-emerald-700"}`}>↑ {showRate}% from prior stage</div>
                                    ) : s.rate != null ? (
                                        <div className="mt-1 text-[11px] text-slate-600 font-medium">— prior stage</div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Toolkit Assessment Completion strip — abandonment recovery */}
                <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="completion-strip">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                        <div>
                            <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Toolkit Assessment Completion</div>
                            <div className="mt-1 font-serif text-xl text-navy-900">Toolkit Purchased → Assessment Started → Assessment Completed</div>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl px-5 py-3 text-right">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-800">Completion Rate</div>
                            <div className="mt-1 font-serif text-2xl text-emerald-900" data-testid="stat-completion-pct">
                                {Math.min(100, Number(stats?.funnel?.toolkit_to_assessment_completed || 0))}%
                            </div>
                            <div className="text-[10px] text-emerald-800/70 mt-0.5">
                                of paid toolkits finished
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="bg-slate-50 rounded-xl p-4" data-testid="completion-purchased">
                            <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-slate-700">Toolkit Purchased</div>
                            <div className="mt-2 font-serif text-2xl text-navy-900">{stats.toolkit_purchased_after_appointment || 0}</div>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4" data-testid="completion-started">
                            <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-slate-700">Assessment Started</div>
                            <div className="mt-2 font-serif text-2xl text-navy-900">{stats.assessment_started || 0}</div>
                            {stats?.funnel?.toolkit_to_assessment_started != null && (
                                <div className="mt-1 text-[11px] text-emerald-700 font-medium">{Math.min(100, stats.funnel.toolkit_to_assessment_started)}% of paid</div>
                            )}
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4" data-testid="completion-completed">
                            <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-slate-700">Assessment Completed</div>
                            <div className="mt-2 font-serif text-2xl text-navy-900">{stats.assessment_completed || 0}</div>
                            {stats?.funnel?.started_to_completed != null && (
                                <div className="mt-1 text-[11px] text-emerald-700 font-medium">{Math.min(100, stats.funnel.started_to_completed)}% of started</div>
                            )}
                        </div>
                        <div className="bg-amber-50 rounded-xl p-4" data-testid="completion-recovery">
                            <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-amber-800">Recovery Nudges Sent</div>
                            <div className="mt-2 font-serif text-2xl text-amber-900">{(stats.recovery_email_1_sent || 0) + (stats.recovery_email_2_sent || 0)}</div>
                            <div className="mt-1 text-[11px] text-amber-800/80 font-medium">
                                24h: {stats.recovery_email_1_sent || 0} · 72h: {stats.recovery_email_2_sent || 0}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toolkit Downloads — per-deliverable activity */}
                <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="toolkit-downloads-stats">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                        <div>
                            <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Toolkit Downloads</div>
                            <div className="mt-1 font-serif text-xl text-navy-900">Retirement Readiness Toolkit&trade; · Deliverable Activity</div>
                        </div>
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl px-5 py-3 text-right">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800">Total Downloads</div>
                            <div className="mt-1 font-serif text-2xl text-amber-900" data-testid="stat-toolkit-downloads-total">
                                {stats.toolkit_downloads_total || 0}
                            </div>
                            <div className="text-[10px] text-amber-900 mt-0.5" data-testid="stat-toolkit-first-downloads">
                                {stats.toolkit_first_downloads || 0} unique buyers · first-download alerts sent
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                            { id: "income-gap-worksheet", number: 1, title: "Retirement Income Gap Worksheet", status: "available" },
                            { id: "social-security-guide", number: 2, title: "Social Security Maximization Guide", status: "available" },
                            { id: "tax-checklist", number: 3, title: "Retirement Tax Reduction Checklist", status: "available" },
                            { id: "rmd-planner", number: 4, title: "RMD & Withdrawal Sequencing Planner", status: "available" },
                            { id: "medicare-planner", number: 5, title: "Medicare & Healthcare Cost Planner", status: "available" },
                            { id: "estate-checklist", number: 6, title: "Estate & Legacy Planning Checklist", status: "available" },
                            { id: "document-organizer", number: 7, title: "Retirement Document Organizer", status: "available" },
                            { id: "rollover-checklist", number: 8, title: "IRA & 401(k) Rollover Checklist", status: "available" },
                            { id: "precious-metals-guide", number: 9, title: "Gold and Silver IRA Guide", status: "available" },
                            { id: "annuity-owners-guide", number: 10, title: "Annuity Owner's Guide", status: "available" },
                        ].map((d) => {
                            const count = stats?.toolkit_downloads_by_deliverable?.[d.id] || 0;
                            const available = d.status === "available";
                            return (
                                <div
                                    key={d.id}
                                    className={`rounded-xl p-4 border ${available ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
                                    data-testid={`toolkit-stat-${d.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-slate-700">#{d.number}</div>
                                            <div className="mt-1 text-sm font-semibold text-navy-900 leading-tight">{d.title}</div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide uppercase whitespace-nowrap ${available ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                            {available ? "Live" : "Soon"}
                                        </span>
                                    </div>
                                    <div className="mt-3 font-serif text-2xl text-navy-900" data-testid={`toolkit-count-${d.id}`}>{count}</div>
                                    <div className="text-[11px] text-slate-700">downloads</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Cross-sell click tracking + attribution */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="cross-sell-stats">
                    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7">
                        <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Cross-Sell Clicks</div>
                        <div className="mt-1 font-serif text-lg text-navy-900">Outbound link tracking</div>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="stat-clicks-annuity">
                                <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-emerald-800">Annuity Review</div>
                                <div className="mt-2 font-serif text-2xl text-emerald-900">{stats.clicks_to_annuity_review || 0}</div>
                                <div className="text-[11px] text-emerald-800/70 mt-0.5">clicks from Toolkit → Review</div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="stat-clicks-augusta">
                                <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-amber-800">Augusta (Affiliate)</div>
                                <div className="mt-2 font-serif text-2xl text-amber-900">{stats.clicks_to_augusta || 0}</div>
                                <div className="text-[11px] text-amber-900 mt-0.5">clicks to Gold and Silver IRA</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7">
                        <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Funnel Attribution</div>
                        <div className="mt-1 font-serif text-lg text-navy-900">Wealth Report → Annuity Review</div>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            <div className="bg-navy-50 border border-slate-200 rounded-xl p-4" data-testid="stat-review-from-wealth">
                                <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-slate-600">Review Requests (Wealth buyers)</div>
                                <div className="mt-2 font-serif text-2xl text-navy-900">{stats.review_requests_from_wealth_buyers || 0}</div>
                                <div className="text-[11px] text-slate-700 mt-0.5">matched by buyer email</div>
                            </div>
                            <div className="bg-navy-50 border border-slate-200 rounded-xl p-4" data-testid="stat-review-utm-wealth">
                                <div className="uppercase tracking-[0.16em] text-[10px] font-bold text-slate-600">UTM-attributed</div>
                                <div className="mt-2 font-serif text-2xl text-navy-900">{stats.review_requests_utm_wealth || 0}</div>
                                <div className="text-[11px] text-slate-700 mt-0.5">utm_source=wealth_report</div>
                            </div>
                        </div>
                    </div>
                </div>

                <Tabs defaultValue="leads" className="mt-10">
                    <TabsList className="bg-white border border-slate-200 p-1">
                        <TabsTrigger value="leads" data-testid="tab-leads"><Users className="w-4 h-4 mr-2" />Leads</TabsTrigger>
                        <TabsTrigger value="conversions" data-testid="tab-conversions"><BarChart3 className="w-4 h-4 mr-2" />Conversions</TabsTrigger>
                    </TabsList>

                    <TabsContent value="leads" className="mt-4">
                        <div id="leads-table-anchor" className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
                            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                                <div className="flex items-center gap-3 flex-1 flex-wrap">
                                    <div className="relative flex-1 min-w-[200px] max-w-md">
                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                                        <Input data-testid="search-input" placeholder="Search name, email, phone, company..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11" />
                                    </div>
                                    <div className="hidden sm:flex items-center gap-2 text-slate-700"><Filter className="w-4 h-4" /></div>
                                    <Select value={status} onValueChange={setStatus}>
                                        <SelectTrigger className="w-44 h-11" data-testid="filter-status"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Select value={agentFilter} onValueChange={setAgentFilter}>
                                        <SelectTrigger className="w-52 h-11" data-testid="filter-agent"><SelectValue placeholder="All agents" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="All">All agents</SelectItem>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <button
                                        type="button"
                                        onClick={() => setHotOnly((v) => !v)}
                                        data-testid="hot-filter-pill"
                                        className={`inline-flex items-center gap-1.5 h-11 px-4 rounded-md border text-sm font-semibold transition-all ${hotOnly ? "bg-rose-500 text-white border-rose-500 shadow-md" : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"}`}
                                    >
                                        <Flame className="w-4 h-4" /> Hot only{stats.hot > 0 ? ` · ${stats.hot}` : ""}
                                    </button>
                                </div>
                                <div className="text-sm text-slate-700">{leads.length} result{leads.length === 1 ? "" : "s"}</div>
                            </div>

                            <div className="mt-6 -mx-2 sm:mx-0 overflow-x-auto">
                                <Table data-testid="leads-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Phone</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Value</TableHead>
                                            <TableHead>Goal</TableHead>
                                            <TableHead>Agent</TableHead>
                                            <TableHead>File</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {/* Only show "Loading…" when there is no prior data to show.
                                            On re-loads (filter change, refresh), keep the previous
                                            rows visible to avoid flicker / layout shift. */}
                                        {loading && leads.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-700">Loading…</TableCell></TableRow>}
                                        {!loading && leads.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-700">No leads.</TableCell></TableRow>}
                                        {leads.map((l) => (
                                            <TableRow key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => { setSelected(l); setComm({}); setNoteText(""); }} data-testid={`lead-row-${l.id}`}>
                                                <TableCell className="font-medium text-navy-900">
                                                    <div className="flex items-center gap-2">
                                                        <span>{l.full_name}</span>
                                                        <LeadEngagementBadge lead={l} />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-slate-700">{l.email}</TableCell>
                                                <TableCell className="text-slate-700">{l.phone}</TableCell>
                                                <TableCell className="text-slate-700">{l.annuity_type}</TableCell>
                                                <TableCell className="text-slate-700">{l.current_value ? `$${Number(l.current_value).toLocaleString()}` : "—"}</TableCell>
                                                <TableCell className="text-slate-700">{l.goal || "—"}</TableCell>
                                                <TableCell className="text-slate-700 text-sm">{l.assigned_agent_name || <span className="text-slate-600">Unassigned</span>}</TableCell>
                                                <TableCell>{l.file_url ? <FileText className="w-4 h-4 text-navy-900" /> : <span className="text-slate-300">—</span>}</TableCell>
                                                <TableCell className="text-slate-700 text-sm whitespace-nowrap">{formatDate(l.timestamp)}</TableCell>
                                                <TableCell>
                                                    <div className="inline-flex items-center gap-1.5">
                                                        <Badge className={statusTone[l.status] || statusTone.New}>{l.status}</Badge>
                                                        {l.auto_promoted_at && (
                                                            <span title={l.auto_promotion_reason || "Auto-promoted by email engagement"} className="text-[9px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 px-1 py-0.5 rounded" data-testid="auto-promoted-badge">auto</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="conversions" className="mt-4">
                        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-serif text-2xl text-navy-900">Conversion by Agent</h3>
                                <div className="text-sm text-slate-700"><TrendingUp className="w-4 h-4 inline mr-1" />Unassigned pool: {conversions.unassigned}</div>
                            </div>
                            <Table data-testid="conversions-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Agent</TableHead>
                                        <TableHead>Total Leads</TableHead>
                                        <TableHead>Sold</TableHead>
                                        <TableHead>Conv Rate</TableHead>
                                        <TableHead>Commission</TableHead>
                                        <TableHead>Override</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {conversions.by_agent.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-700">No agents yet.</TableCell></TableRow>}
                                    {conversions.by_agent.map((r) => (
                                        <TableRow key={r.agent_id} data-testid={`conv-row-${r.agent_id}`}>
                                            <TableCell className="font-medium text-navy-900">{r.agent_name}</TableCell>
                                            <TableCell>{r.total_leads}</TableCell>
                                            <TableCell>{r.sold}</TableCell>
                                            <TableCell>{r.conversion_rate}%</TableCell>
                                            <TableCell>${r.commission_total.toLocaleString()}</TableCell>
                                            <TableCell className="text-emerald-700 font-medium">${r.override_total.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lead-detail-sheet">
                    {selected && (
                        <>
                            <SheetHeader>
                                <SheetTitle className="font-serif text-2xl text-navy-900">{selected.full_name}</SheetTitle>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <Badge className={statusTone[selected.status] || statusTone.New}>{selected.status}</Badge>
                                    {selected.auto_promoted_at && (
                                        <span title={selected.auto_promotion_reason || "Auto-promoted by email engagement"} className="text-[9px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">auto</span>
                                    )}
                                    <span className="text-sm text-slate-700">· {formatDate(selected.timestamp)}</span>
                                    {selected.assigned_agent_name && <span className="text-sm text-navy-900">· {selected.assigned_agent_name}</span>}
                                </div>
                            </SheetHeader>

                            <div className="mt-6 space-y-2">
                                <Row icon={Mail} label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                                <Row icon={Phone} label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />
                                <Row icon={Building2} label="Company" value={selected.company || "—"} />
                                <Row icon={DollarSign} label="Current value" value={selected.current_value ? `$${Number(selected.current_value).toLocaleString()}` : "—"} />
                                <Row icon={Calendar} label="Purchase year" value={selected.purchase_year || "—"} />
                            </div>

                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <Field label="Inquiry type" value={formatInquiryType(selected.inquiry_type)} highlight={!!selected.inquiry_type} />
                                <Field label="Annuity Type" value={selected.annuity_type || "—"} />
                                <Field label="Goal" value={selected.goal || "—"} />
                                <Field label="Income Rider" value={selected.income_rider || "—"} />
                                <Field label="Surrender Known" value={selected.surrender_charge_known || "—"} />
                                <Field label="State" value={selected.state || "—"} />
                                <Field label="ZIP" value={selected.zip_code || "—"} />
                                <Field label="Lead source" value={formatLeadSource(selected.lead_source, selected.lead_source_detail)} />
                                {selected.agent_slug && <Field label="Agent landing" value={`/agent/${selected.agent_slug}`} />}
                            </div>

                            <AppointmentCard appointment={selected.appointment} />

                            {(selected.income_confidence || selected.ltc_concern || selected.currently_taking_income ||
                              selected.financial_situation_changed || selected.marital_status || selected.review_focus) && (
                                <div className="mt-8 border-t border-slate-200 pt-6" data-testid="admin-needs-section">
                                    <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Retirement picture</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {selected.income_confidence && <Field label="Enough income" value={selected.income_confidence} />}
                                        {selected.ltc_concern && <Field label="LTC concern" value={selected.ltc_concern} />}
                                        {selected.currently_taking_income && <Field label="Taking income" value={selected.currently_taking_income} />}
                                        {selected.financial_situation_changed && <Field label="Situation changed" value={selected.financial_situation_changed} />}
                                        {selected.marital_status && <Field label="Married/partnered" value={selected.marital_status} />}
                                        {selected.marital_status === "Yes" && selected.spouse_has_annuity && <Field label="Spouse has annuity" value={selected.spouse_has_annuity} />}
                                    </div>
                                    {selected.review_focus && (
                                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
                                            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-800 font-semibold">Wants us to look at</div>
                                            <div className="mt-1 text-slate-900 text-sm whitespace-pre-wrap">{selected.review_focus}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <LeadEngagementCard lead={selected} />

                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Assign Agent</div>
                                <Select value={selected.assigned_agent_id || ""} onValueChange={(v) => assign(v === "__unassign" ? null : v)}>
                                    <SelectTrigger data-testid="assign-agent-select"><SelectValue placeholder="Choose agent..." /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__unassign">— Unassign —</SelectItem>
                                        {agents.filter((a) => a.is_active).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Update Status</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {STATUSES.filter((s) => s !== "All").map((s) => (
                                        <Button key={s} disabled={updating || selected.status === s} onClick={() => updateStatus(selected, s)} data-testid={`status-btn-${s.replace(/\s+/g, "-")}`} className={selected.status === s ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}>{s}</Button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Commission Tracking</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <CommField label="Lead Fee ($)" k="lead_fee" current={selected.lead_fee} comm={comm} setComm={setComm} testid="comm-lead-fee" />
                                    <div>
                                        <Label className="text-xs text-slate-600">Sale Status</Label>
                                        <Select value={comm.sale_status ?? selected.sale_status ?? ""} onValueChange={(v) => setComm({ ...comm, sale_status: v })}>
                                            <SelectTrigger data-testid="comm-sale-status" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                                            <SelectContent>
                                                {SALE_STATUSES.map((s) => <SelectItem key={s || "none"} value={s || "none"}>{s || "— None —"}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <CommField label="Estimated Premium ($)" k="estimated_premium" current={selected.estimated_premium} comm={comm} setComm={setComm} testid="comm-premium" />
                                    <CommField label="Commission Generated ($)" k="commission_generated" current={selected.commission_generated} comm={comm} setComm={setComm} testid="comm-generated" />
                                    <CommField label="Override %" k="override_percent" current={selected.override_percent} comm={comm} setComm={setComm} testid="comm-override-pct" />
                                    <Field label="Override Amount (auto)" value={selected.override_amount != null ? `$${Number(selected.override_amount).toLocaleString()}` : "—"} highlight />
                                </div>
                                <Button onClick={saveCommission} className="mt-4 bg-navy-900 hover:bg-navy-800 text-white w-full" data-testid="comm-save">Save Commission</Button>
                            </div>

                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3 flex items-center"><MessageSquare className="w-4 h-4 mr-1" />Notes</div>
                                <div className="space-y-3 max-h-56 overflow-y-auto">
                                    {(selected.notes || []).length === 0 && <div className="text-sm text-slate-700">No notes yet.</div>}
                                    {(selected.notes || []).map((n) => (
                                        <div key={n.id || n.at} className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                                            <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
                                            <div className="text-xs text-slate-700 mt-1">{n.by} · {formatDate(n.at)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 flex items-start gap-2">
                                    <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." data-testid="admin-note-input" />
                                    <Button onClick={addNote} disabled={!noteText.trim()} className="bg-navy-900 text-white" data-testid="admin-note-submit"><Send className="w-4 h-4" /></Button>
                                </div>
                            </div>

                            {selected.file_url && (
                                <div className="mt-8 border-t border-slate-200 pt-6">
                                    <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Statement</div>
                                    <a
                                        data-testid="download-file-link"
                                        className="mt-2 inline-flex items-center gap-2 text-navy-900 underline"
                                        href={`${API}/admin/files/${selected.file_url}`}
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            try {
                                                const r = await api.get(`/admin/files/${selected.file_url}`, { responseType: "blob" });
                                                const url = URL.createObjectURL(r.data);
                                                window.open(url, "_blank");
                                                setTimeout(() => URL.revokeObjectURL(url), 10000);
                                            } catch (err) { toast.error(formatApiError(err)); }
                                        }}
                                    >
                                        <FileText className="w-4 h-4" /> {selected.file_name || "View statement"}
                                    </a>
                                </div>
                            )}

                            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-slate-600 hover:text-slate-700" data-testid="close-sheet">
                                <X className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </SheetContent>
            </Sheet>
            <DangerZone />
        </div>
    );
}

function DangerZone() {
    const [confirmText, setConfirmText] = useState("");
    const [open, setOpen] = useState(false);
    const [working, setWorking] = useState(false);
    const [preview, setPreview] = useState(null);

    const openDialog = async () => {
        setOpen(true);
        setConfirmText("");
        setPreview(null);
        try {
            const r = await api.post("/admin/reset-test-data", null, { params: { dry_run: true } });
            setPreview(r.data);
        } catch (err) {
            toast.error(`Could not preview reset: ${formatApiError(err)}`);
        }
    };

    const executeReset = async () => {
        if (confirmText !== "RESET") {
            toast.error("Type RESET exactly to confirm.");
            return;
        }
        setWorking(true);
        try {
            const r = await api.post("/admin/reset-test-data", null, { params: { confirm: "YES" } });
            toast.success(`Reset complete · deleted ${r.data.deleted_test_leads_actual || 0} test leads`);
            setOpen(false);
            setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setWorking(false);
        }
    };

    return (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 pb-12 mt-16" data-testid="admin-danger-zone">
            <div className="rounded-2xl border-2 border-rose-300 bg-rose-50/60 p-6 sm:p-8">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-rose-900">Danger zone — Reset all stats</h3>
                        <p className="mt-1 text-sm text-rose-900/80 leading-relaxed">
                            Wipes all test leads, downloads, and cross-sell click tracking from this database.
                            Real Stripe-purchased customers, payment_transactions, and admin/agent config are preserved.
                            Use this once before launch to zero out your dashboard.
                        </p>
                        <button
                            onClick={openDialog}
                            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm shadow-sm"
                            data-testid="danger-reset-stats-button"
                        >
                            <AlertTriangle className="w-4 h-4" /> Reset all stats
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="danger-reset-modal"
                    onClick={(e) => e.target === e.currentTarget && !working && setOpen(false)}
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                                <ShieldAlert className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">Confirm reset</h3>
                        </div>

                        {preview ? (
                            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm space-y-1.5">
                                <div className="flex justify-between"><span className="text-slate-700">Test leads to delete:</span> <span className="font-mono font-bold text-rose-700">{preview.deleted_test_leads}</span></div>
                                <div className="flex justify-between"><span className="text-slate-700">Real paid leads preserved:</span> <span className="font-mono font-bold text-emerald-700">{preview.preserved_real_leads}</span></div>
                                <div className="flex justify-between"><span className="text-slate-700">Toolkit downloads to wipe:</span> <span className="font-mono font-bold text-rose-700">{preview.wiped_counter_collections?.toolkit_downloads ?? 0}</span></div>
                                <div className="flex justify-between"><span className="text-slate-700">Cross-sell clicks to wipe:</span> <span className="font-mono font-bold text-rose-700">{preview.wiped_counter_collections?.cross_sell_clicks ?? 0}</span></div>
                                <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5"><span className="text-slate-700">Stripe records kept:</span> <span className="font-mono font-bold text-emerald-700">{preview.kept_payment_transactions}</span></div>
                            </div>
                        ) : (
                            <p className="mt-4 text-sm text-slate-600">Calculating impact…</p>
                        )}

                        <div className="mt-5">
                            <Label htmlFor="reset-confirm-input" className="text-sm text-slate-700">Type <span className="font-bold font-mono text-rose-700">RESET</span> to confirm:</Label>
                            <Input
                                id="reset-confirm-input"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="RESET"
                                className="mt-2"
                                autoComplete="off"
                                data-testid="danger-reset-confirm-input"
                            />
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => setOpen(false)}
                                disabled={working}
                                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium disabled:opacity-50"
                                data-testid="danger-reset-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeReset}
                                disabled={working || confirmText !== "RESET"}
                                className="px-5 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="danger-reset-execute"
                            >
                                {working ? "Resetting…" : "Yes, wipe everything"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function CommField({ label, k, current, comm, setComm, testid }) {
    const value = comm[k] ?? (current ?? "");
    return (
        <div>
            <Label className="text-xs text-slate-600">{label}</Label>
            <Input
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => setComm({ ...comm, [k]: e.target.value })}
                className="mt-1"
                data-testid={testid}
            />
        </div>
    );
}

function StatCard({ label, value, icon: Icon, accent = "bg-slate-100 text-slate-700", active = false, onClick, ringColor = "ring-navy-100 border-navy-500", testid }) {
    const baseClass = "text-left bg-white border-2 rounded-2xl p-5 transition-all w-full";
    const stateClass = onClick
        ? (active
            ? `${ringColor} ring-2 shadow-lg`
            : "border-slate-200 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300")
        : "border-slate-200";
    const content = (
        <>
            <div className="flex items-center justify-between">
                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">{label}</div>
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
            </div>
            <div className="mt-4 font-serif text-3xl text-navy-900">{value}</div>
            {onClick && (
                <div className="mt-1 text-[11px] text-slate-700">{active ? "Filter active · click to clear" : "Click to filter"}</div>
            )}
        </>
    );
    if (onClick) {
        return (
            <button type="button" onClick={onClick} data-testid={testid} className={`${baseClass} ${stateClass}`}>
                {content}
            </button>
        );
    }
    return <div data-testid={testid} className={`${baseClass} ${stateClass}`}>{content}</div>;
}

function scrollToTable() {
    const el = document.getElementById("leads-table-anchor");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Row({ icon: Icon, label, value, link }) {
    const inner = <span className="text-slate-800">{value}</span>;
    return (
        <div className="flex items-start gap-3 py-2">
            <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
            <div className="flex-1">
                <div className="text-xs text-slate-700">{label}</div>
                <div className="text-base">{link ? <a href={link} className="hover:underline">{inner}</a> : inner}</div>
            </div>
        </div>
    );
}

function Field({ label, value, highlight }) {
    return (
        <div className={`${highlight ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"} rounded-md px-4 py-3`}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-700 font-semibold">{label}</div>
            <div className={`mt-1 ${highlight ? "text-emerald-800" : "text-slate-900"} text-sm font-medium`}>{value}</div>
        </div>
    );
}

function AppointmentCard({ appointment }) {
    if (!appointment || !appointment.start_time) return null;
    const isCanceled = !!appointment.canceled_at;
    const tone = isCanceled ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50";
    const label = isCanceled ? "Appointment canceled" : "Appointment booked";
    const titleColor = isCanceled ? "text-rose-900" : "text-emerald-900";
    return (
        <div className={`mt-6 border-2 rounded-xl p-5 ${tone}`} data-testid="lead-appointment-card">
            <div className="flex items-center gap-2 mb-3">
                <div className={`text-[11px] uppercase tracking-[0.2em] font-bold ${titleColor}`}>{label}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Start</div>
                    <div className="mt-0.5 text-slate-900 font-medium">{formatDate(appointment.start_time)}</div>
                </div>
                {appointment.end_time && (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">End</div>
                        <div className="mt-0.5 text-slate-900 font-medium">{formatDate(appointment.end_time)}</div>
                    </div>
                )}
                {isCanceled && (
                    <>
                        <div className="col-span-2">
                            <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Canceled by</div>
                            <div className="mt-0.5 text-rose-900 font-medium">{appointment.canceled_by || "—"}</div>
                        </div>
                        {appointment.cancellation_reason && (
                            <div className="col-span-2">
                                <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Reason</div>
                                <div className="mt-0.5 text-rose-900">{appointment.cancellation_reason}</div>
                            </div>
                        )}
                    </>
                )}
            </div>
            {!isCanceled && (appointment.reschedule_url || appointment.cancel_url) && (
                <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    {appointment.reschedule_url && (
                        <a href={appointment.reschedule_url} target="_blank" rel="noreferrer" className="text-emerald-800 hover:text-emerald-700 underline" data-testid="appt-reschedule-link">
                            Reschedule
                        </a>
                    )}
                    {appointment.cancel_url && (
                        <a href={appointment.cancel_url} target="_blank" rel="noreferrer" className="text-rose-700 hover:text-rose-600 underline" data-testid="appt-cancel-link">
                            Cancel booking
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
}

const LEAD_SOURCE_LABELS = {
    "google": "Google search",
    "google-ads": "Google ad",
    "facebook": "Facebook / Instagram",
    "tiktok": "TikTok",
    "youtube": "YouTube",
    "referral": "Friend referral",
    "agent-page": "Agent landing page",
    "email": "Email / newsletter",
    "direct": "Direct visit",
    "other": "Other",
};

const INQUIRY_TYPE_LABELS = {
    "review-existing": "Review existing annuity",
    "first-purchase": "First-time purchase",
    "additional-purchase": "Adding another annuity",
    "ira-roth-conversion": "IRA → Roth conversion",
    "cd-replacement": "CD replacement",
    "just-questions": "General questions",
};

function formatInquiryType(t) {
    if (!t) return "—";
    return INQUIRY_TYPE_LABELS[t] || t;
}

function formatLeadSource(source, detail) {
    if (!source) return "—";
    const label = LEAD_SOURCE_LABELS[source] || source;
    return detail ? `${label} · ${detail}` : label;
}


/**
 * Compact Stripe-mode pill rendered inline in the dashboard header.
 * Color-coded so live mode is unmistakable at a glance:
 *   • LIVE  → solid green (charging real cards)
 *   • TEST  → amber (test cards only, no real revenue)
 *   • UNCONFIGURED → red (no STRIPE_API_KEY)
 */
function StripeModePill({ mode, webhookSecretSet }) {
    if (!mode) return null;
    const m = String(mode).toLowerCase();
    const isLive = m === "live";
    const isMissing = m === "unconfigured";
    const cls = isLive
        ? "bg-emerald-600 text-white border-emerald-700"
        : isMissing
            ? "bg-rose-600 text-white border-rose-700"
            : "bg-amber-100 text-amber-900 border-amber-300";
    const Icon = isLive ? ShieldCheck : ShieldAlert;
    const label = isMissing ? "UNCONFIGURED" : m.toUpperCase();
    return (
        <div
            data-testid="stripe-mode-pill"
            title={isLive
                ? `Live Stripe key in use${webhookSecretSet ? "" : " — webhook secret missing"}`
                : isMissing
                    ? "STRIPE_API_KEY is empty"
                    : "Test mode — no real charges"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[11px] font-bold tracking-wide ${cls}`}
        >
            <Icon className="w-3.5 h-3.5" />
            Stripe Mode: <span data-testid="stripe-mode-value">{label}</span>
        </div>
    );
}

/**
 * Full-width banner below the header. Severity adapts to the mode:
 *   • LIVE + webhook secret set  → calm green confirmation
 *   • LIVE + secret missing      → loud amber warning (real money + spoofable webhook)
 *   • TEST                       → muted amber info banner
 *   • UNCONFIGURED               → loud red error
 */
function StripeModeBanner({ mode, webhookSecretSet }) {
    if (!mode) return null;
    const m = String(mode).toLowerCase();

    let severity = "test";
    let title = "";
    let body = "";
    if (m === "live" && webhookSecretSet) {
        severity = "ok";
        title = "Stripe Mode: LIVE";
        body = "Real cards are being charged. Webhook signature verification is enabled.";
    } else if (m === "live" && !webhookSecretSet) {
        severity = "warn";
        title = "Stripe Mode: LIVE \u00b7 Webhook secret missing";
        body = "Real cards are being charged, but STRIPE_WEBHOOK_SECRET is empty in backend/.env. Set it to verify Stripe webhook signatures before scaling traffic.";
    } else if (m === "test") {
        severity = "test";
        title = "Stripe Mode: TEST";
        body = "Test keys are in use \u2014 no real charges. Flip STRIPE_API_KEY to your live key when you're ready.";
    } else {
        severity = "danger";
        title = "Stripe Mode: UNCONFIGURED";
        body = "STRIPE_API_KEY is empty. Checkout will fail until a key is set.";
    }

    const styles = {
        ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
        warn: "bg-amber-50 border-amber-300 text-amber-900",
        test: "bg-amber-50/60 border-amber-200 text-amber-900",
        danger: "bg-rose-50 border-rose-300 text-rose-900",
    }[severity];
    const Icon = severity === "ok" ? ShieldCheck : severity === "danger" ? ShieldAlert : AlertTriangle;

    return (
        <div data-testid="stripe-mode-banner" className={`mb-6 flex items-start gap-3 px-5 py-3.5 rounded-xl border ${styles}`}>
            <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm leading-relaxed">
                <div className="font-semibold" data-testid="stripe-mode-banner-title">{title}</div>
                <div className="text-[13px] opacity-90 mt-0.5">{body}</div>
            </div>
        </div>
    );
}
